<template>
  <section v-if="showHero" class="hero">
    <div class="hero-content">
      <h1>Réservation confirmée</h1>
      <p>
        Votre paiement a bien été pris en compte. Retrouvez ci-dessous les informations essentielles de votre trajet.
      </p>
    </div>
  </section>

  <div class="page-container page-content">
    <div v-if="isBootstrapping" class="ticket-loading">
      <LoaderOverlay />
      <p>Vérification de l’état du paiement…</p>
    </div>

    <template v-else>
      <EmptyState
        v-if="isValidated"
        variant="ticketValidated"
        :validated-at="formattedValidatedAt"
        :payer-phone="ticketData.payerPhone"
      />

      <EmptyState
        v-else-if="!ticketData && !error"
        :is-empty="true"
        empty-message="Aucun résultat"
      />

      <EmptyState
        v-else-if="ticketData && ticketData.status === 'pending_payment'"
        variant="pendingPayment"
        empty-message="Votre paiement est en cours de validation…"
      />

      <EmptyState
        v-else-if="ticketData && ticketData.status === 'cancelled'"
        variant="cancelledPayment"
        empty-message="Votre paiement a été annulé."
      />

      <template v-else-if="ticketData && !isValidated">
          <div class="card card-details no-shadow">
                <div class="card-item">
                  <strong>Départ</strong>
                  <div class="card-item--address">
                    <span>{{ formatDateTime(ticketData.timeToStart) }}</span>
                    <strong class="city">{{ ticketData.cityAtDeparture }}</strong>
                    <span class="card-item--address--full">{{ ticketData.addressAtDeparture }}</span>
                  </div>
                </div>
                <div class="card-item">
                  <strong>Arrivée</strong>
                  <div class="card-item--address">
                    <span>{{ formatDateTime(ticketData.timeToEnd) }}</span>
                    <strong class="city">{{ ticketData.cityAtArrival }}</strong>
                    <span class="card-item--address--full">{{ ticketData.addressAtArrival }}</span>
                  </div>
                </div>
              <div v-if="ticketData.company" class="card-item">
                <strong>Compagnie</strong>
                <span>{{ ticketData.company }}</span>
              </div>
              <div class="card-item card-item--last">
                <strong>Classe</strong>
                <span>{{ classLabel }}</span>
              </div>
              </div>
      </template>

      <EmptyState
        v-else
        :error="error"
        :is-empty="!error"
        empty-message="Aucun résultat"
      />
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import LoaderOverlay from '@/components/LoaderOverlay.vue'
import { useModalStore } from '@/stores/modalStore'
import { useToast } from 'vue-toast-notification'
import EmptyState from '@/components/EmptyState.vue'
import { config } from '@/config/env.js'

const route = useRoute()
const modalStore = useModalStore()
const toast = useToast()

const loading = ref(true)
const isBootstrapping = ref(true)
const ticketData = ref(null)
const error = ref(null)

let pollingTimeout = null
let isPolling = false
let paymentModalHandle = null

const isValidated = computed(() => {
  if (!ticketData.value) return false
  const validatedAt = ticketData.value.validatedAt
  return validatedAt != null && validatedAt !== ''
})

const classLabel = computed(() => {
  const travelClass = ticketData.value?.classe
  if (travelClass === null || travelClass === 0 || travelClass === undefined) {
    return 'Classic'
  }
  if (travelClass === 1) {
    return 'Premium'
  }
  return 'Classic'
})

const showHero = computed(() => {
  return !isBootstrapping.value &&
    !loading.value &&
    !error.value &&
    !!ticketData.value &&
    ticketData.value.status === 'paid'
})

function parseApiDateTime(raw) {
  if (!raw) return null
  if (typeof raw !== 'string') return new Date(raw)
  return new Date(raw.replace(' ', 'T'))
}

const formattedValidatedAt = computed(() => {
  const raw = ticketData.value?.validatedAt
  if (!raw) return ''
  const d = parseApiDateTime(raw)
  if (!d || Number.isNaN(d.getTime())) return String(raw)
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(d)
})

async function fetchTicket() {
  loading.value = true

  try {
    error.value = null

    const response = await fetch(`${config.apiUrl}/ticket/${route.params.ticketUuid}`, {
      method: 'GET',
      credentials: 'omit',
      headers: {
        Accept: 'application/json'
      }
    })

    const result = await response.json().catch(() => ({}))

    if (!response.ok) {
      throw new Error(result.error || result.message || `Erreur HTTP ${response.status}`)
    }

    ticketData.value = result

    if (result.status === 'pending_payment' && !paymentModalHandle) {
      paymentModalHandle = modalStore.open({
        kind: 'alert',
        title: 'Paiement en attente',
        message: 'Votre paiement est en cours de validation.\nCette page sera mise à jour automatiquement.',
        showConfirm: false,
        showCancel: true,
        cancelText: 'Annuler',
        dismissible: true,
        closeOnBackdrop: false,
        closeOnEsc: true
      })
    }

    if (result.status === 'paid') {
      if (paymentModalHandle) {
        paymentModalHandle.close()
        paymentModalHandle = null
        toast.success('Paiement validé avec succès !')
      }
      stopPolling()
      return true
    }

    if (result.status === 'cancelled') {
      if (paymentModalHandle) {
        paymentModalHandle.close()
        paymentModalHandle = null
      }
      error.value = 'Le paiement a été annulé.'
      toast.error('Le paiement a échoué. Veuillez réessayer.')
      stopPolling()
      return true
    }

    return false
  } catch (e) {
    console.error('⚠️ Erreur fetch:', e)
    error.value = e.message
    toast.error(`Erreur: ${e.message}`)
    stopPolling()
    return true
  } finally {
    loading.value = false
    isBootstrapping.value = false
  }
}

function scheduleNextPoll() {
  if (!isPolling) return

  pollingTimeout = setTimeout(async () => {
    if (!isPolling) return
    const shouldStop = await fetchTicket()
    if (!shouldStop && isPolling) {
      scheduleNextPoll()
    }
  }, 5000)
}

function startPolling() {
  if (isPolling) return
  isPolling = true
  scheduleNextPoll()
}

function stopPolling() {
  isPolling = false
  if (pollingTimeout) {
    clearTimeout(pollingTimeout)
    pollingTimeout = null
  }
}

onMounted(async () => {
  const shouldStop = await fetchTicket()

  if (!shouldStop && ticketData.value?.status !== 'paid' && ticketData.value?.status !== 'cancelled') {
    startPolling()
  }
})

onUnmounted(() => {
  stopPolling()

  if (paymentModalHandle) {
    paymentModalHandle.close()
    paymentModalHandle = null
  }
})

function formatDateTime(dateString) {
  if (!dateString) return '—'

  const date = new Date(dateString)

  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}
</script>

<style scoped>
.page-content {
  max-width: 700px;
  margin: 0 auto;
}

:deep(.empty-state) {
  margin-top: 4rem;
}

:deep(.empty-state-content .empty-state-icon) {
  width: 200px;
  height: 200px;
}

/* .ticket-loading { */
/*   min-height: 14rem; */
/*   display: flex; */
/*   flex-direction: column; */
/*   align-items: center; */
/*   justify-content: center; */
/*   gap: 1rem; */
/* } */
/**/
/* .ticket-loading p { */
/*   margin: 0; */
/*   text-align: center; */
/* } */
/**/
/* .ticket-content { */
/*   width: 100%; */
/*   max-width: 760px; */
/*   margin: 0 auto; */
/* } */
/**/
/**/
/* .card { */
/*   padding: 0; */
/*   margin: 0; */
/*   border: 2px solid var(--color-border); */
/*   border-radius: var(--border-radius-md); */
/* } */
/**/
/* .card .card-content { */
/*   display: flex; */
/*   flex-direction: column; */
/* } */
/**/
/* .ticket-grid { */
/*   display: grid; */
/*   grid-template-columns: repeat(2, minmax(0, 1fr)); */
/* } */
/**/
/* .card .card-item { */
/*   display: flex; */
/*   justify-content: space-between; */
/*   gap: 1rem; */
/*   padding: 0.8rem 1.5rem; */
/*   border-bottom: 1px solid var(--color-border); */
/*   align-items: flex-start; */
/* } */
/**/
/* .card .card-item:nth-child(even) { */
/*   background-color: var(--color-grey-light); */
/* } */
/**/
/* .card .card-item.card-item--last { */
/*   border-bottom: none; */
/* } */
/**/
.card .card-item--address {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  align-items: flex-end;
  text-align: right;
}
strong.city {
  text-transform: uppercase;
}
/**/
@media (max-width: 768px) {
  /* .ticket-grid { */
  /*   grid-template-columns: 1fr; */
  /* } */
  /**/
  :deep(.empty-state) {
    margin-top: 2rem;
  }
}
</style>
